import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DashboardService } from 'src/app/shared/services/dashboardservices/dashboard.service';
import { ResignationService } from 'src/app/shared/services/resignservices/resignation.service';
import { SharedModeService } from 'src/app/shared/services/shared-mode.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-resignation-list',
  templateUrl: './resignation-list.component.html',
  styleUrls: ['./resignation-list.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule]
})
export class ResignationListComponent implements OnInit {
  usersList: any[] = [];
  showSpinner = false;
  user: any;
  userInfo: any;
  colormode: any;
  selectedMode: any;
  datamode: any;
  stats = {
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    thisMonth: 0
  };

  selectedResignation: any = null;
  acceptDateOption: 'same' | 'change' = 'same';
  customLastWorkingDate = '';

  employeeOptions: any[] = [];
  terminationSubmitting = false;
  terminationDropdownOpen = false;
  terminationEmployeeSearch = '';
  terminationForm = {
    user_id: '',
    subject: '',
    resign_explanation: '',
    last_working_date: '',
    additional_notes: ''
  };

  constructor(
    private resignationService: ResignationService,
    public dashboardService: DashboardService,
    private sharedmode: SharedModeService,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.user = JSON.parse(localStorage.getItem('user') || '');
    this.user_detail();

    const user_role = JSON.parse(this.user.user_role.user_roles);
    // if (user_role.role_type != 'Admin') {
    //   this.router.navigate(['/dashboard-1']);
    // }
  }

  ngOnInit(): void {
    this.showSpinner = true;
    this.getAllResignations();
    this.loadEmployeesForTermination();
    this.sharedmode.data$.subscribe(mode => {
      this.selectedMode = mode;
    });
  }

  toggleMode(mode: string) {
    this.datamode;
    this.sharedmode.setMode(mode);
  }

  getAllResignations() {
    this.resignationService.getResignationList().subscribe(response => {
      this.showSpinner = false;
      if (response.success) {
        this.usersList = (response.data || [])
          .map((item: any) => this.normalizeResignationRow(item))
          .sort((a: any, b: any) => {
            if (a.status === 'pending' && b.status !== 'pending') return -1;
            if (a.status !== 'pending' && b.status === 'pending') return 1;
            return 0;
          });
        this.calculateStats();
      } else {
        Swal.fire({
          iconHtml: ' <img src="../../../../assets/images/error-modal.svg"  alt="image" >',
          title: response.message,
          showConfirmButton: false,
          timer: 3000,
        });
      }
    });
  }

  calculateStats() {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    this.stats.total = this.usersList.length;
    this.stats.pending = this.usersList.filter(u => u.status === 'pending').length;
    this.stats.accepted = this.usersList.filter(u => u.status === 'accepted').length;
    this.stats.rejected = this.usersList.filter(u => u.status === 'rejected').length;
    this.stats.thisMonth = this.usersList.filter(u => {
      const resignDate = new Date(u.resign_date);
      return resignDate.getMonth() === currentMonth && resignDate.getFullYear() === currentYear;
    }).length;
  }

  normalizeResignationRow(item: any) {
    return {
      ...item,
      type: item?.type || 'resignation',
      subject: item?.subject || '',
      additional_notes: item?.additional_notes || '',
    };
  }

  loadEmployeesForTermination() {
    this.dashboardService.getUserList().then((value: any) => {
      if (!value?.success || !value?.data) {
        return;
      }

      const flattenedUsers: any[] = [];
      const groupedUsers = value.data;
      Object.keys(groupedUsers).forEach((department) => {
        (groupedUsers[department] || []).forEach((employee: any) => {
          if (employee?.removed == 1) {
            return;
          }
          flattenedUsers.push(employee);
        });
      });

      this.employeeOptions = flattenedUsers.sort((a, b) => {
        const aName = `${a?.first_name || ''} ${a?.last_name || ''}`.trim();
        const bName = `${b?.first_name || ''} ${b?.last_name || ''}`.trim();
        return aName.localeCompare(bName);
      });
    });
  }

  openTerminationModal() {
    this.terminationForm = {
      user_id: '',
      subject: '',
      resign_explanation: '',
      last_working_date: '',
      additional_notes: '',
    };
    this.terminationDropdownOpen = false;
    this.terminationEmployeeSearch = '';
  }

  toggleTerminationDropdown() {
    this.terminationDropdownOpen = !this.terminationDropdownOpen;
  }

  closeTerminationDropdown() {
    this.terminationDropdownOpen = false;
  }

  get filteredTerminationEmployees() {
    const query = (this.terminationEmployeeSearch || '').trim().toLowerCase();
    if (!query) {
      return this.employeeOptions;
    }

    return this.employeeOptions.filter((employee: any) => {
      const fullName = `${employee?.first_name || ''} ${employee?.last_name || ''}`.toLowerCase();
      const email = `${employee?.email || ''}`.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
  }

  selectTerminationEmployee(employee: any) {
    this.terminationForm.user_id = String(employee?.id || '');
    this.terminationEmployeeSearch = '';
    this.closeTerminationDropdown();
  }

  get selectedTerminationEmployeeLabel() {
    const selected = this.employeeOptions.find((employee: any) => String(employee?.id) === String(this.terminationForm.user_id));
    if (!selected) {
      return 'Select employee';
    }

    return `${selected.first_name || ''} ${selected.last_name || ''} (${selected.email || ''})`.trim();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as Node;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.closeTerminationDropdown();
    }
  }

  submitTermination() {
    if (this.terminationSubmitting) {
      return;
    }

    if (!this.terminationForm.user_id || !this.terminationForm.subject || !this.terminationForm.resign_explanation || !this.terminationForm.last_working_date) {
      Swal.fire({
        iconHtml: ' <img src="../../../../assets/images/warning_icon.svg"  alt="image" >',
        title: 'Please fill all required fields.',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const model = {
      ...this.terminationForm,
      last_working_date: `${this.terminationForm.last_working_date} 00:00:00`,
    };

    this.terminationSubmitting = true;
    this.resignationService.addTermination(model).subscribe({
      next: (response: any) => {
        this.terminationSubmitting = false;
        if (response.success) {
          ($('#terminateEmployeeModal') as any).modal('hide');
          this.getAllResignations();
          Swal.fire({
            iconHtml: ' <img src="../../../../assets/images/success-modal.svg"  alt="image" >',
            title: 'Termination Request Submitted Successfully!',
            showConfirmButton: false,
            timer: 3000,
          });
        } else {
          Swal.fire({
            iconHtml: ' <img src="../../../../assets/images/error-modal.svg"  alt="image" >',
            title: response.message,
            showConfirmButton: false,
            timer: 3000,
          });
        }
      },
      error: (error: any) => {
        this.terminationSubmitting = false;
        Swal.fire({
          iconHtml: ' <img src="../../../../assets/images/error-modal.svg"  alt="image" >',
          title: error?.error?.message || 'Unable to submit termination request. Please try again.',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    });
  }

  getTypeLabel(item: any) {
    return (item?.type || 'resignation') === 'termination' ? 'Termination' : 'Resignation';
  }

  getTypeClass(item: any) {
    return (item?.type || 'resignation') === 'termination' ? 'type-termination' : 'type-resignation';
  }

  getRequestNoun(item: any) {
    return (item?.type || 'resignation') === 'termination' ? 'termination' : 'resignation';
  }

  openAcceptModal(user: any) {
    this.selectedResignation = user;
    this.acceptDateOption = 'same';
    this.customLastWorkingDate = '';
  }

  confirmAcceptResignation() {
    if (!this.selectedResignation) return;

    let finalDate = this.selectedResignation.last_working_date;

    if (this.acceptDateOption === 'change') {
      if (!this.customLastWorkingDate) return;
      finalDate = this.customLastWorkingDate;
    }

    const model = {
      user_id: this.selectedResignation.user_id,
      id: this.selectedResignation.id,
      status: 'accepted',
      last_working_date: finalDate
    };

    ($('#acceptResignationModal') as any).modal('hide');
    this.acceptStatusUpdated(model, this.selectedResignation);
  }

  updateAcceptStatus(user: any) {
    const model = {
      user_id: user.user_id,
      id: user.id,
      status: 'accepted'
    };
    Swal.fire({
      iconHtml: ' <img src="../../../../assets/images/warning_icon.svg"  alt="image" >',
      title: `Do you really want to accept this ${this.getRequestNoun(user)}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Accepted',
      showConfirmButton: true,
      cancelButtonText: 'Not Now'
    }).then((response: any) => {
      if (response.value) {
        this.acceptStatusUpdated(model, user);
      }
    });
  }

  acceptStatusUpdated(model: any, requestRecord?: any) {
    this.resignationService.updateResignStatus(model).subscribe(response => {
      if (response.success) {
        this.getAllResignations();
        Swal.fire({
          iconHtml: ' <img src="../../../../assets/images/success-modal.svg"  alt="image" >',
          title: `${this.getTypeLabel(requestRecord || this.selectedResignation || model)} Accepted Successfully!`,
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          iconHtml: ' <img src="../../../../assets/images/error-modal.svg"  alt="image" >',
          title: response.message,
          showConfirmButton: false,
          timer: 3000,
        });
      }
    });
  }

  updateRejectStatus(user: any) {
    const model = {
      user_id: user.user_id,
      id: user.id,
      status: 'rejected'
    };
    Swal.fire({
      iconHtml: ' <img src="../../../../assets/images/warning_icon.svg"  alt="image" >',
      title: `Do you really want to reject this ${this.getRequestNoun(user)}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Rejected',
      showConfirmButton: true,
      cancelButtonText: 'Not Now'
    }).then((response: any) => {
      if (response.value) {
        this.rejectedStatusUpdated(model, user);
      }
    });
  }

  rejectedStatusUpdated(model: any, requestRecord?: any) {
    this.resignationService.updateResignStatus(model).subscribe(response => {
      if (response.success) {
        this.getAllResignations();
        Swal.fire({
          iconHtml: ' <img src="../../../../assets/images/success-modal.svg"  alt="image" >',
          title: `${this.getTypeLabel(requestRecord || model)} Rejected Successfully!`,
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          iconHtml: ' <img src="../../../../assets/images/error-modal.svg"  alt="image" >',
          title: response.message,
          showConfirmButton: false,
          timer: 3000,
        });
      }
    });
  }

  user_detail() {
    this.showSpinner = true;
    this.dashboardService.getuserdetail().then((value) => {
      this.userInfo = value.data;
      this.user = this.userInfo;
      this.colormode = this.userInfo.color_mode;
      this.sharedmode.setMode(this.colormode);
      this.showSpinner = false;
    });
  }

  isavailablefeature(type: string): boolean {
    switch (type) {
      case 'isView':
        var user_role = JSON.parse(this.user.user_role.user_roles);
        return !user_role.resign_data.data.values.view === false;
      case 'isAccept':
        var user_role = JSON.parse(this.user.user_role.user_roles);
        return !user_role.resign_data.data.values.accept === false;
      case 'isReject':
        var user_role = JSON.parse(this.user.user_role.user_roles);
        return !user_role.resign_data.data.values.reject === false;
      default:
        return false;
    }
  }
}
